import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Provider, useStore } from 'react-redux';
import { FACEDOWN, PLAYER_STATUS } from 'constants/gameConstants';
import { GameProvider } from 'contexts/GameContext';
import { HandHistorySnapshotModeContext } from 'contexts/HandHistorySnapshotModeContext';
import { OrientationOverrideProvider } from 'contexts/OrientationOverrideContext';
import { ShowAllDealersContext } from 'contexts/ShowAllDealersContext';
import { TableIdContext } from 'contexts/TableIdContext';
import {
  buildTableDemo,
  Currency,
  GameVariant,
  MAX_DEMO_SEATS,
  NINE_SEAT_DEMO_POV_GUID,
  PotScenario,
  SeatCardMode
} from 'demo/nineSeatTableDemo';
import { Orientation } from 'hooks/useOrientation';
import {
  currentTableSlice,
  updateCurrentPlayerGUID,
  updateCurrentTable
} from 'reducers/currentTable';
import { patchPlayerProfile } from 'reducers/user';
import { createAppStore, RootState } from 'store';

import Table from 'components/Table';

import { formatPlayerDisplayName } from './format';
import { useSnapshotFrameHug } from './snapshotFrameHug';
import { snapshotHandRank } from './snapshotHandRank';
import {
  BettingStructure,
  CardRef,
  GameType,
  GameVariantId,
  HandMetadata,
  PotGroupPlayerSnapshot,
  PotGroupSnapshot,
  TableSnapshot as TableSnapshotType
} from './types';
import { winningFiveFor } from './winningCards';

import styles from './HandHistory.module.scss';

type SnapshotWinner = {
  seat: number;
  win: number;
  cards: string;
  hand: string;
  handType: string;
};

/**
 * Where each recorded seat sits on the snapshot table, and how many seats the
 * table has. The capture records the table's size (`totalSeats`, the `seats`
 * column), so the snapshot draws that table — every player in their real seat,
 * the empty seats open — as the live felt does. A record without a usable size
 * (absent, smaller than a seat number, larger than the felt can draw) keeps the
 * older compact layout: the players side by side, nothing open.
 */
export const snapshotSeatLayout = (snapshot: TableSnapshotType) => {
  const sorted = [...snapshot.seats].sort((a, b) => a.seat - b.seat);
  const size = Number(snapshot.totalSeats);
  const highest = sorted.length > 0 ? sorted[sorted.length - 1].seat : 0;
  const real =
    Number.isInteger(size) &&
    size >= Math.max(2, highest) &&
    size <= MAX_DEMO_SEATS &&
    sorted.every((seat) => Number.isInteger(seat.seat) && seat.seat >= 1);

  return {
    seatCount: real ? size : Math.max(2, sorted.length),
    demoSeatOf: new Map(sorted.map((seat, index) => [seat.seat, real ? seat.seat : index + 1]))
  };
};

const seatModeForSnapshot = (snapshot: TableSnapshotType, demoSeatOf: Map<number, number>) =>
  [...snapshot.seats]
    .sort((a, b) => a.seat - b.seat)
    .reduce<Record<number, SeatCardMode>>((modes, seat, index) => {
      const demoSeat = demoSeatOf.get(seat.seat) ?? index + 1;

      if (seat.folded) {
        modes[demoSeat] = 'fold';
        return modes;
      }

      modes[demoSeat] = seat.cardsRevealed ? 'show' : 'down';
      return modes;
    }, {});

const withoutBets = (
  payload: ReturnType<typeof buildTableDemo>,
  dealerSeat: number | undefined
) => {
  const next: ReturnType<typeof buildTableDemo> = {
    ...payload,
    game: {
      ...payload.game,
      dealer: dealerSeat ? String(dealerSeat) : '',
      dealerVisible: Boolean(dealerSeat),
      move: '',
      sbPlayer: '',
      bbPlayer: ''
    },
    players: payload.players.map((player: any) => ({
      ...player,
      bet: '',
      lbet: 0,
      betDisplay: '',
      action: ''
    }))
  };

  for (let seat = 1; seat <= 10; seat++) {
    next.game[`p${seat}bet`] = '';
    next.game[`p${seat}lbet`] = 0;
    next.game[`p${seat}action`] = '';
  }

  return next;
};

/**
 * The pot a snapshot shows, when it shows exactly one: a carousel slide is one
 * pot, so the felt's pot pill names it exactly as the carousel title and the
 * payout group do ("Side Pot 1", "Side Pot 1 - Lo"). Undefined for the main
 * pot and for a snapshot of several pots, which keep the felt's own label as
 * before ("Main Pot", or "Pot" when it is the only one).
 */
export const snapshotPotName = (potGroups: PotGroupSnapshot[]) => {
  const payouts = potGroups.filter(
    (pot) => pot.potType !== 'no_payout' && pot.name !== 'No Payout'
  );
  return payouts.length === 1 && payouts[0].name !== 'Main Pot' ? payouts[0].name : undefined;
};

const withSnapshotWinners = (
  payload: ReturnType<typeof buildTableDemo>,
  winners: SnapshotWinner[],
  potTotal: number,
  sidePotAmounts: number[] | null,
  potName?: string
) => {
  return {
    ...payload,
    game: {
      ...payload.game,
      handHistorySnapshot: true,
      // The demo scaffold supplies a hard-coded live hand-rank label. Snapshot
      // results come from the recorded winners, never from that placeholder.
      handRank: '',
      pot: potTotal,
      totalPot: potTotal,
      showdown: {
        ...payload.game.showdown,
        currentPot: 0,
        currentStep: 3
      },
      // With side pots the demo scaffold's pots carry placeholder amounts; the
      // recorded amounts replace them, one pot per recorded pot.
      pots: (sidePotAmounts
        ? payload.game.pots
            .slice(0, sidePotAmounts.length)
            .map((pot: any, index: number) => ({ ...pot, pot: sidePotAmounts[index] }))
        : payload.game.pots
      ).map((pot: any, index: number) =>
        index === 0
          ? {
              ...pot,
              ...(potName ? { label: potName } : {}),
              pot: sidePotAmounts ? pot.pot : potTotal,
              winners: winners.map((winner) => ({
                player: String(winner.seat),
                cards: winner.cards,
                hand: winner.hand,
                win: winner.win,
                handType: winner.handType
              }))
            }
          : pot
      )
    },
    gameState: {
      ...payload.gameState,
      isShowdown: true
    }
  };
};

const normalizeCard = (card: string | undefined) => {
  if (!card || card === FACEDOWN) return '';
  return card.toUpperCase();
};

const getBoardStage = (boardLength: number) => {
  if (boardLength >= 5) return 'river';
  if (boardLength === 4) return 'turn';
  if (boardLength >= 3) return 'flop';
  return 'preflop';
};

const winnerHandType = (potName: string) => {
  if (/\blo\b/i.test(potName)) return 'LO';
  if (/\bhi\b/i.test(potName)) return 'HI';
  return '';
};

/**
 * The five the winner banner shows and lights: the record's five, else the
 * best five of the winner's cards on this board — never all seven cards, which
 * the banner would draw in full.
 */
const winnerCards = (
  player: PotGroupPlayerSnapshot,
  board: CardRef[],
  gameVariant: GameVariantId
) => {
  if (!player.cardsRevealed || player.folded || player.mucked) return '';
  const cards = player.winningCards?.length
    ? player.winningCards
    : winningFiveFor(player, board, gameVariant) ?? [];

  return cards.map(normalizeCard).filter(Boolean).join(',');
};

const isNoPayoutGroup = (pot: PotGroupSnapshot) =>
  pot.potType === 'no_payout' || pot.name === 'No Payout';

// A side pot exists only when the record says so; Hi/Lo halves of one pot and
// the No Payout list are not side pots.
const hasSidePot = (potGroups: PotGroupSnapshot[]) =>
  potGroups.some(
    (pot) => !isNoPayoutGroup(pot) && (pot.potType === 'side' || /^side pot\b/i.test(pot.name))
  );

// One amount per recorded pot, in record order: the Hi and Lo halves of a
// split pot ("Main Pot - HI" / "Main Pot - LO") are one pot on the felt.
const recordedPotAmounts = (potGroups: PotGroupSnapshot[]) => {
  const amounts = new Map<string, number>();
  potGroups
    .filter((pot) => !isNoPayoutGroup(pot))
    .forEach((pot) => {
      const name = pot.name.replace(/\s*-\s*(HI|LO)$/i, '');
      amounts.set(name, Math.round(((amounts.get(name) ?? 0) + pot.amount) * 100) / 100);
    });
  return Array.from(amounts.values());
};

/**
 * What the winner banner names: the record's hand, else — for a high winner who
 * showed — the category their cards make on THIS board. Multi-run hands stored
 * before the capture parsed a single-pot board's payout line have no hand on
 * any run's winner row (screenshot #13); without this their snapshots show no
 * banner at all. A low half is named only by the record (`loLabel`).
 */
const winnerHand = (
  player: PotGroupPlayerSnapshot,
  board: CardRef[],
  gameVariant: GameVariantId
) => {
  const recorded = player.handRank ?? player.loLabel;
  if (recorded) return recorded;
  if (player.handType === 'Lo' || !player.cardsRevealed || player.folded || player.mucked) {
    return '';
  }
  return snapshotHandRank(player.cards ?? [], board, gameVariant);
};

/** A seat the recorded hand left empty, drawn as an open seat (no truthy `ID`). */
const openSeat = {
  GUID: '',
  ID: '',
  stack: 0,
  bet: '',
  lbet: 0,
  betDisplay: '',
  action: '',
  status: 0,
  openAddFunds: false,
  totalBounty: 0,
  totalHandBounty: 0,
  isMuted: 0,
  displayName: '',
  duration: '',
  avatar: ''
};

const buildGrossWinBySeat = (potGroups: PotGroupSnapshot[], seatToDemoSeat: Map<number, number>) =>
  potGroups.reduce<Record<number, number>>((totals, pot) => {
    const winners = pot.players.filter((player) => player.isWinner || player.net > 0);
    const positiveTotal = winners.reduce((sum, player) => sum + Math.max(player.net, 0), 0);

    winners.forEach((player) => {
      const demoSeat = seatToDemoSeat.get(player.seat);
      if (!demoSeat) return;

      const share =
        typeof player.grossWin === 'number' && player.grossWin >= 0
          ? player.grossWin
          : positiveTotal > 0
          ? (Math.max(player.net, 0) / positiveTotal) * pot.amount
          : pot.amount / Math.max(winners.length, 1);
      totals[demoSeat] = (totals[demoSeat] ?? 0) + share;
    });

    return totals;
  }, {});

const withSnapshotSeats = (
  payload: ReturnType<typeof buildTableDemo>,
  seats: TableSnapshotType['seats'],
  board: CardRef[],
  grossWinBySeat: Record<number, number>,
  demoSeatOf: Map<number, number>,
  viewerSeat?: number
) => {
  const seatAt = new Map(seats.map((seat) => [demoSeatOf.get(seat.seat), seat]));
  const visibleSeats: number[] = [];
  const showCardSeats: number[] = [];
  const nextGame = { ...payload.game };
  const boardCards = board.map(normalizeCard);

  for (let cardIndex = 0; cardIndex < 5; cardIndex++) {
    nextGame[`card${cardIndex + 1}`] = boardCards[cardIndex] ?? '';
  }

  const nextPlayers = payload.players.map((player: any, index: number) => {
    if (index === 0) return player;

    const seat = seatAt.get(index);
    if (!seat) {
      // Nobody sat here in the recorded hand: an open seat, as on the felt.
      nextGame[`p${index}bet`] = '';
      nextGame[`p${index}lbet`] = 0;
      nextGame[`p${index}action`] = '';
      for (let cardIndex = 0; cardIndex < 4; cardIndex++) {
        nextGame[`p${index}card${cardIndex + 1}`] = '';
      }
      return { ...openSeat };
    }

    const isViewer = seat.seat === viewerSeat;
    const folded = Boolean(seat.folded);
    const mucked = Boolean(seat.mucked);
    const cards = folded ? [] : seat.cards?.map(normalizeCard).filter(Boolean) ?? [];
    const hasCards = cards.length > 0;
    const action = folded ? 'fold' : '';

    nextGame[`p${index}bet`] = folded ? 'F' : '';
    nextGame[`p${index}lbet`] = 0;
    nextGame[`p${index}action`] = action;
    nextGame[`p${index}status`] = mucked ? PLAYER_STATUS.MUCK_CARDS : PLAYER_STATUS.ACTIVE;

    for (let cardIndex = 0; cardIndex < 4; cardIndex++) {
      nextGame[`p${index}card${cardIndex + 1}`] = cards[cardIndex] ?? '';
    }

    if (hasCards) {
      visibleSeats.push(index);
    }

    if (hasCards && seat.cardsRevealed) {
      showCardSeats.push(index);
    }

    return {
      ...player,
      GUID: isViewer ? NINE_SEAT_DEMO_POV_GUID : `hand-history-seat-${seat.seat}`,
      ID: index,
      stack: Math.max((seat.stack ?? player.stack) - (grossWinBySeat[index] ?? 0), 0),
      bet: folded ? 'F' : '',
      lbet: 0,
      betDisplay: '',
      action,
      status: mucked ? PLAYER_STATUS.MUCK_CARDS : PLAYER_STATUS.ACTIVE,
      displayName: formatPlayerDisplayName(seat.name, isViewer),
      avatar: seat.avatar ?? player.avatar
    };
  });

  return {
    ...payload,
    game: {
      ...nextGame,
      seatsWithVisibleCards: visibleSeats,
      seatsWithShowCard: showCardSeats
    },
    players: nextPlayers
  };
};

const withSnapshotMetadata = (
  payload: ReturnType<typeof buildTableDemo>,
  metadata: HandMetadata,
  bettingStructure: BettingStructure,
  gameVariant: GameVariantId,
  gameType: GameType
) => {
  const smallBlind = metadata.smallBlind ?? payload.game.sb;
  const bigBlind = metadata.bigBlind ?? payload.game.bb;
  const variantLabel =
    gameVariant === 'omaha' ? (bettingStructure.includes('HiLo') ? 'PLO8' : 'PLO') : 'NLH';

  // Same mapping as the preview adapter (HandHistoryDemoTable): TableDetails
  // reads tabletype 's' as cash, anything else as a tournament level in chips.
  return {
    ...payload,
    game: {
      ...payload.game,
      game: gameVariant === 'omaha' ? 'omaha' : 'holdem',
      gameType: bettingStructure,
      tabletype: gameType === 'cash' ? 's' : 't',
      sb: smallBlind,
      bb: bigBlind,
      ante: metadata.ante ?? 0,
      lvlDisplay: metadata.level ?? 1,
      currentSB: smallBlind,
      currentBB: bigBlind,
      currentAnte: metadata.ante ?? 0,
      nextLevel: -1,
      blindLevels:
        gameType === 'cash'
          ? `$${smallBlind}/$${bigBlind} ${variantLabel}`
          : `Level ${metadata.level ?? 1} · ${smallBlind}/${bigBlind}${
              metadata.ante ? ` (${metadata.ante})` : ''
            } ${variantLabel}`
    }
  };
};

const getSnapshotOrientation = (): Orientation => {
  if (typeof window === 'undefined') return 'portrait';

  const isMobileLandscape = window.innerWidth <= 900 && window.innerWidth > window.innerHeight;

  return isMobileLandscape ? 'landscape' : 'portrait';
};

export const TableSnapshot = ({
  snapshot,
  gameType,
  gameVariant,
  bettingStructure,
  metadata,
  potTotal,
  potGroups
}: {
  snapshot: TableSnapshotType;
  gameType: GameType;
  gameVariant: GameVariantId;
  bettingStructure: BettingStructure;
  metadata: HandMetadata;
  potTotal: number;
  potGroups: PotGroupSnapshot[];
}) => {
  const parentStore = useStore<RootState>();
  const [snapshotOrientation, setSnapshotOrientation] = useState(getSnapshotOrientation);
  const sortedSeats = useMemo(
    () => [...snapshot.seats].sort((a, b) => a.seat - b.seat),
    [snapshot.seats]
  );
  const layout = useMemo(() => snapshotSeatLayout(snapshot), [snapshot]);
  const playerCount = layout.seatCount;
  const seatToDemoSeat = layout.demoSeatOf;
  const viewerSeat =
    (snapshot.viewerSeat !== undefined ? seatToDemoSeat.get(snapshot.viewerSeat) : undefined) ?? 1;
  const variant: GameVariant = gameVariant;
  const potScenario: PotScenario = hasSidePot(potGroups)
    ? 'side'
    : potTotal > 0 && snapshot.seats.length > 2
    ? 'showdown'
    : 'main';
  const sidePotAmounts = useMemo(
    () => (potScenario === 'side' ? recordedPotAmounts(potGroups) : null),
    [potGroups, potScenario]
  );
  const currency: Currency = gameType === 'cash' ? 'cents' : 'dollars';
  const seatModes = useMemo(
    () => seatModeForSnapshot(snapshot, seatToDemoSeat),
    [seatToDemoSeat, snapshot]
  );
  const dealerSeat = useMemo(() => {
    const sourceDealerSeat = snapshot.dealerSeat ?? sortedSeats.find((seat) => seat.isDealer)?.seat;

    return sourceDealerSeat ? seatToDemoSeat.get(sourceDealerSeat) : undefined;
  }, [seatToDemoSeat, snapshot.dealerSeat, sortedSeats]);
  const grossWinBySeat = useMemo(
    () => buildGrossWinBySeat(potGroups, seatToDemoSeat),
    [potGroups, seatToDemoSeat]
  );
  const snapshotWinners = useMemo(() => {
    const winnersBySeat = new Map<number, SnapshotWinner>();

    potGroups.forEach((pot) => {
      pot.players
        .filter((player) => player.isWinner || player.net > 0)
        .forEach((player) => {
          const demoSeat = seatToDemoSeat.get(player.seat);
          if (!demoSeat || winnersBySeat.has(demoSeat)) return;

          winnersBySeat.set(demoSeat, {
            seat: demoSeat,
            win: grossWinBySeat[demoSeat] ?? Math.max(player.net, 0),
            cards: winnerCards(player, snapshot.board, gameVariant),
            hand: winnerHand(player, snapshot.board, gameVariant),
            handType: player.handType ?? winnerHandType(pot.name)
          });
        });
    });

    return Array.from(winnersBySeat.values());
  }, [gameVariant, grossWinBySeat, potGroups, seatToDemoSeat, snapshot.board]);

  useEffect(() => {
    const handleResize = () => setSnapshotOrientation(getSnapshotOrientation());

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const snapshotStore = useMemo(() => {
    const store = createAppStore({
      ...parentStore.getState(),
      currentTable: currentTableSlice.getInitialState(),
      tables: {}
    });
    const payload = withSnapshotWinners(
      withSnapshotSeats(
        withoutBets(
          withSnapshotMetadata(
            buildTableDemo(
              playerCount,
              potScenario,
              Math.max(1, (sidePotAmounts?.length ?? 2) - 1),
              currency,
              0,
              viewerSeat,
              true,
              seatModes,
              getBoardStage(snapshot.board.length),
              false,
              {},
              variant,
              0
            ),
            metadata,
            bettingStructure,
            gameVariant,
            gameType
          ),
          dealerSeat
        ),
        sortedSeats,
        snapshot.board,
        grossWinBySeat,
        seatToDemoSeat,
        snapshot.viewerSeat
      ),
      snapshotWinners,
      potTotal,
      sidePotAmounts,
      snapshotPotName(potGroups)
    );

    const viewer = sortedSeats.find((seat) => seat.seat === snapshot.viewerSeat);
    payload.game.handRank =
      viewer && !viewer.folded && !viewer.mucked
        ? snapshotHandRank(viewer.cards || [], snapshot.board, gameVariant)
        : '';
    store.dispatch(patchPlayerProfile({ playerGUID: NINE_SEAT_DEMO_POV_GUID }));
    store.dispatch(updateCurrentPlayerGUID(NINE_SEAT_DEMO_POV_GUID));
    store.dispatch(updateCurrentTable(payload));
    return store;
  }, [
    bettingStructure,
    currency,
    dealerSeat,
    gameType,
    gameVariant,
    grossWinBySeat,
    metadata,
    parentStore,
    potTotal,
    potScenario,
    playerCount,
    seatModes,
    seatToDemoSeat,
    sidePotAmounts,
    snapshot.board,
    snapshot.board.length,
    snapshot.viewerSeat,
    snapshotWinners,
    sortedSeats,
    potGroups,
    variant,
    viewerSeat
  ]);

  // The frame hugs what the table paints: no empty band above open top seats,
  // room only for a badge or glow that is actually there. See snapshotFrameHug.
  const stageRef = useRef<HTMLDivElement>(null);
  useSnapshotFrameHug(stageRef);

  return (
    <div className={styles.snapshot}>
      <div className={styles.realTableFrame}>
        <div ref={stageRef} className={styles.realTableStage} data-snapshot-stage=''>
          <Provider store={snapshotStore}>
            <TableIdContext.Provider value=''>
              <OrientationOverrideProvider orientation={snapshotOrientation}>
                <ShowAllDealersContext.Provider value={false}>
                  <HandHistorySnapshotModeContext.Provider value>
                    {/* Not a live table: its hand-history provider must open no socket listeners. */}
                    <GameProvider handHistorySource='demo'>
                      <Table />
                    </GameProvider>
                  </HandHistorySnapshotModeContext.Provider>
                </ShowAllDealersContext.Provider>
              </OrientationOverrideProvider>
            </TableIdContext.Provider>
          </Provider>
        </div>
      </div>
    </div>
  );
};
